import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircleIcon,
  StarIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  CalendarIcon,
  CreditCardIcon,
  ClockIcon,
  ClipboardDocumentIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import { supabase, getAdminStatusCached } from './lib/supabase';
import Toast, { useToast } from './Toast';
import Header from './Header';
import Footer from './Footer';
import AuthModal from './AuthModal';
import WhatsAppButton from './WhatsAppButton';

function MembershipPage() {
  const [user, setUser] = useState(null);
  const [, setUserProfile] = useState(null);
  const [activeMembership, setActiveMembership] = useState(null);
  const [, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [, setShowPurchaseModal] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmedPlan, setConfirmedPlan] = useState(null);
  const { toast, setToast, showToast } = useToast();

  const handleBackToHome = () => {
    setShowConfirmation(false);
    setConfirmedPlan(null);
    setSelectedPlan(null);
    window.location.href = '/';
  };

  useEffect(() => {
    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setUser(session.user);
          await Promise.all([
            fetchUserProfile(session.user.email),
            fetchActiveMembership(session.user.id)
          ]);
        }
      } catch (error) {
        console.error('Error checking user:', error);
      } finally {
        setLoading(false);
      }
    };
    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'TOKEN_REFRESHED') return;

      try {
        if (session?.user) {
          setUser(session.user);
          await Promise.all([
            fetchUserProfile(session.user.email),
            fetchActiveMembership(session.user.id)
          ]);
        } else {
          setUser(null);
          setUserProfile(null);
          setActiveMembership(null);
        }
      } catch (error) {
        console.error('Auth state change error:', error);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserProfile = async (email) => {
    const { data, error } = await getAdminStatusCached(email);
    if (!error && data?.[0]) {
      setUserProfile(data[0]);
    }
  };

  const fetchActiveMembership = async (userId) => {
    try {
      const { data, error } = await supabase
        .rpc('get_active_membership', { user_uuid: userId });

      if (!error && data?.[0]) {
        setActiveMembership(data[0]);
      } else {
        setActiveMembership(null);
      }
    } catch (error) {
      console.error('Error fetching membership:', error);
      setActiveMembership(null);
    }
  };

  const handlePurchaseMembership = async (plan) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    try {
      const amount = plan.id === 'monthly' ? 1200 : plan.id === 'coaching' ? 2500 : 12000;
      
      const { error } = await supabase
        .rpc('create_membership', {
          p_user_id: user.id,
          p_membership_type: plan.id,
          p_amount_paid: amount
        });

      if (error) {
        showToast('Error creating membership: ' + error.message, 'error');
        return;
      }

      await fetchActiveMembership(user.id);
      setShowPurchaseModal(false);
      setConfirmedPlan(selectedPlan);
      setShowConfirmation(true);
    } catch (error) {
      console.error('Error purchasing membership:', error);
      showToast('Error purchasing membership. Please try again.', 'error');
    }
  };

  const membershipPlans = [
    {
      id: 'monthly',
      type: 'Monthly Membership',
      price: '₹1,200',
      duration: 'per month',
      originalPrice: '₹1,500',
      savings: 'Save ₹300',
      features: [
        'Exclusive booking slots (5-9AM & 6-9PM)',
        'Priority access during peak hours'
      ],
      popular: true,
      buttonText: 'Choose Monthly'
    },
    {
      id: 'coaching',
      type: 'Coaching Fees',
      price: '₹2,500',
      duration: 'per month',
      originalPrice: null,
      savings: null,
      features: [
        'Professional coaching sessions',
        'Exclusive booking slots 5-6 PM (Kids)',
        'Classes are held Monday to Friday'
      ],
      popular: false,
      buttonText: 'Choose Coaching'
    },
    {
      id: 'yearly',
      type: 'Yearly Membership',
      price: '₹12,000',
      duration: 'per year',
      originalPrice: '₹15,000',
      savings: 'Save ₹3,000',
      features: [
        'Exclusive booking slots (5-9AM & 6-9PM)',
        'Priority access during peak hours',
        'Annual savings (17% off)'
      ],
      popular: false,
      buttonText: 'Choose Yearly'
    }
  ];

  const benefits = [
    {
      icon: StarIcon,
      title: 'Priority Access',
      description: 'Exclusive access to 5-9AM and 6-9PM time slots'
    },
    {
      icon: UserGroupIcon,
      title: 'Early & Evening Slots',
      description: 'Perfect for working professionals and early risers'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Guaranteed Availability',
      description: 'Skip the booking rush and secure your preferred times'
    },
    {
      icon: CalendarIcon,
      title: 'Flexible Booking',
      description: 'Book premium slots when others cannot'
    }
  ];

  // ─── Confirmation Screen ───
  if (showConfirmation && confirmedPlan) {
    const membershipAmount = confirmedPlan.id === 'monthly' ? 1200 : confirmedPlan.id === 'coaching' ? 2500 : 12000;
    
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />

        <section className="relative bg-gradient-to-br from-green-600 via-green-700 to-green-800 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/20" />
            <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-white/10" />
          </div>
          <div className="relative container mx-auto px-4 py-10 text-center">
            <div className="bg-white/20 backdrop-blur-sm w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 animate-soft-bounce">
              <CheckCircleIcon className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 animate-smooth-rise">Membership Submitted!</h1>
            <p className="text-green-100 text-lg max-w-xl mx-auto animate-smooth-rise animate-delay-200">
              Your {confirmedPlan.type} request is awaiting admin approval.
            </p>
          </div>
        </section>

        <main className="container mx-auto px-4 py-8 max-w-2xl -mt-4">
          {/* Status Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-6 animate-smooth-rise animate-delay-200">
            <div className="flex items-center gap-3 px-6 py-4 bg-amber-50 border-b border-amber-100">
              <ClockIcon className="h-5 w-5 text-amber-600" />
              <span className="font-semibold text-amber-800">Status: Pending Approval</span>
            </div>
            <div className="px-6 py-5">
              <div className="flex justify-between items-center">
                <span className="text-gray-500 text-sm">Total Amount</span>
                <span className="text-3xl font-bold text-gray-900">₹{membershipAmount.toLocaleString()}</span>
              </div>
              <p className="text-sm text-gray-500 mt-2">You'll be notified once your membership is approved.</p>
            </div>
          </div>

          {/* Payment Warning */}
          <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-6 animate-smooth-rise animate-delay-300">
            <h3 className="font-bold text-red-800 mb-3 flex items-center gap-2">
              <ShieldCheckIcon className="h-5 w-5" />
              Payment Instructions
            </h3>
            <ol className="text-sm text-red-700 space-y-2">
              <li className="flex gap-2"><span className="font-bold text-red-800 flex-shrink-0">1.</span> Complete payment using the methods below</li>
              <li className="flex gap-2"><span className="font-bold text-red-800 flex-shrink-0">2.</span> Take a screenshot of your payment confirmation</li>
              <li className="flex gap-2"><span className="font-bold text-red-800 flex-shrink-0">3.</span> Call/WhatsApp admin at <strong>8080517517</strong></li>
              <li className="flex gap-2"><span className="font-bold text-red-800 flex-shrink-0">4.</span> Send payment proof and membership details</li>
              <li className="flex gap-2"><span className="font-bold text-red-800 flex-shrink-0">5.</span> Wait for admin confirmation</li>
            </ol>
          </div>

          {/* Payment Methods */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-6 animate-smooth-rise animate-delay-400">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <CreditCardIcon className="h-5 w-5 text-green-600" />
                Complete Your Payment
              </h3>
            </div>
            <div className="p-6 grid sm:grid-cols-2 gap-4">
              {/* GPay / PhonePe */}
              <div className="border border-gray-200 rounded-xl p-5 hover:border-green-300 transition-colors">
                <h4 className="font-semibold text-gray-800 mb-3">Google Pay / PhonePe</h4>
                <div className="flex items-center justify-between text-sm mb-4">
                  <span className="text-gray-500">Phone:</span>
                  <span className="font-mono font-bold text-green-700">9840728266</span>
                </div>
                <button
                  onClick={() => {
                    const gPayURL = `tez://upi/pay?pa=bestbadmintonclub@cnrb&pn=Best%20Badminton%20Club&am=${membershipAmount}&cu=INR`;
                    window.location.href = gPayURL;
                  }}
                  className="w-full bg-green-600 text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors"
                >
                  Pay with GPay / PhonePe
                </button>
                <p className="text-xs text-gray-400 mt-2 text-center">Works on mobile only</p>
              </div>

              {/* UPI */}
              <div className="border border-gray-200 rounded-xl p-5 hover:border-green-300 transition-colors">
                <h4 className="font-semibold text-gray-800 mb-3">UPI Transfer</h4>
                <div className="mb-4">
                  <span className="text-xs text-gray-500">UPI ID:</span>
                  <div className="font-mono text-sm bg-gray-50 px-3 py-2 rounded-lg border border-gray-200 mt-1 break-all">
                    bestbadmintonclub@cnrb
                  </div>
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText('bestbadmintonclub@cnrb');
                    showToast('UPI ID copied to clipboard!', 'success');
                  }}
                  className="w-full bg-gray-900 text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-800 transition-colors flex items-center justify-center gap-1.5"
                >
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  Copy UPI ID
                </button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3 animate-smooth-rise animate-delay-500">
            <button
              onClick={() => {
                const gPayURL = `tez://upi/pay?pa=bestbadmintonclub@cnrb&pn=Best%20Badminton%20Club&am=${membershipAmount}&cu=INR`;
                window.location.href = gPayURL;
              }}
              className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-3.5 rounded-xl font-semibold hover:from-green-700 hover:to-green-800 transition-all shadow-sm hover:shadow-md"
            >
              Pay Now with GPay / PhonePe
            </button>
            <button
              onClick={handleBackToHome}
              className="w-full bg-white text-gray-700 py-3.5 rounded-xl font-semibold border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              Back to Home
            </button>
            <p className="text-sm text-gray-400 text-center pt-1">
              Don't forget to notify admin via WhatsApp (8080517517)
            </p>
          </div>
        </main>

        <WhatsAppButton />
        <Toast toast={toast} onClose={() => setToast(null)} />
      </div>
    );
  }

  // ─── Main Page ───
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Page Header */}
      <section className="relative bg-gradient-to-br from-green-600 via-green-700 to-green-800 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/20" />
          <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-white/10" />
        </div>
        <div className="relative container mx-auto px-4 pt-10 pb-12 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 animate-smooth-rise">
            {user && activeMembership ? 'Your Membership' : 'Choose Your Membership'}
          </h1>
          <p className="text-green-200 text-lg max-w-2xl mx-auto animate-smooth-rise animate-delay-100">
            {user && activeMembership
              ? 'Enjoy your exclusive member benefits and priority access'
              : 'Get exclusive access to prime time slots (5-9AM & 6-9PM) when regular users cannot book'}
          </p>
        </div>
      </section>

      <main className="container mx-auto px-4 py-10">
        {user && activeMembership ? (
          /* ─── Active Membership View ─── */
          <div className="max-w-3xl mx-auto">
            {/* Membership Card */}
            <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-2xl shadow-lg text-white overflow-hidden mb-6 animate-smooth-rise">
              <div className="p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6">
                  <div>
                    <div className="text-green-200 text-sm font-medium mb-1">Active Plan</div>
                    <h2 className="text-2xl sm:text-3xl font-bold capitalize">{activeMembership.membership_type} Membership</h2>
                    <p className="text-green-200 mt-1">Best Badminton Club</p>
                  </div>
                  <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2.5">
                    <div className="text-xs text-green-200">Member ID</div>
                    <div className="font-mono font-bold text-sm">#{activeMembership.id.slice(-8).toUpperCase()}</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 bg-white/10 rounded-xl p-4">
                  <div>
                    <div className="text-xs text-green-200 mb-0.5">Start Date</div>
                    <div className="font-semibold">
                      {new Date(activeMembership.start_date).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric'
                      })}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-green-200 mb-0.5">End Date</div>
                    <div className="font-semibold">
                      {new Date(activeMembership.end_date).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric'
                      })}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-green-200 mb-0.5">Remaining</div>
                    <div className="font-semibold">{activeMembership.days_remaining} days</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8 mb-6 animate-smooth-rise animate-delay-200">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Your Member Benefits</h3>
              <div className="space-y-3">
                {(activeMembership.membership_type === 'yearly' ? [
                  'Exclusive booking slots (5-9AM & 6-9PM)',
                  'Priority access during peak hours',
                  'Annual savings (17% off)'
                ] : [
                  'Exclusive booking slots (5-9AM & 6-9PM)',
                  'Priority access during peak hours'
                ]).map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3 bg-green-50 border border-green-100 rounded-xl px-4 py-3">
                    <CheckCircleSolidIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-col sm:flex-row gap-3 animate-smooth-rise animate-delay-300">
              <Link
                to="/booking"
                className="flex-1 inline-flex items-center justify-center px-6 py-3.5 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors shadow-sm hover:shadow-md"
              >
                Book Your Court
              </Link>
              <Link
                to="/my-bookings"
                className="flex-1 inline-flex items-center justify-center px-6 py-3.5 bg-white text-gray-700 rounded-xl font-semibold border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                View My Bookings
              </Link>
            </div>
          </div>
        ) : (
          /* ─── Plans View ─── */
          <div>
            {/* Membership Plans */}
            <div className="max-w-5xl mx-auto mb-16">
              <div className="grid md:grid-cols-3 gap-6">
                {membershipPlans.map((plan, index) => (
                  <div
                    key={plan.id}
                    className={`bg-white rounded-2xl shadow-sm border-2 relative flex flex-col h-full overflow-hidden transition-all hover:shadow-md animate-smooth-rise ${
                      plan.popular
                        ? 'border-green-500 ring-4 ring-green-500/10'
                        : 'border-gray-200 hover:border-green-300'
                    }`}
                    style={{ animationDelay: `${(index + 1) * 100}ms` }}
                  >
                    {plan.popular && (
                      <div className="bg-green-600 text-white text-center text-xs font-bold uppercase tracking-wider py-2">
                        Most Popular
                      </div>
                    )}

                    <div className="p-6 sm:p-7 flex flex-col flex-1">
                      <div className="mb-5">
                        <h3 className="text-xl font-bold text-gray-900 mb-3">{plan.type}</h3>
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-4xl font-bold text-green-700">{plan.price}</span>
                          <span className="text-gray-400 text-sm">/{plan.duration.split(' ')[1]}</span>
                        </div>
                        {(plan.originalPrice || plan.savings) && (
                          <div className="flex items-center gap-2 mt-2">
                            {plan.originalPrice && (
                              <span className="text-sm text-gray-400 line-through">{plan.originalPrice}</span>
                            )}
                            {plan.savings && (
                              <span className="text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full ring-1 ring-green-600/20">
                                {plan.savings}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      <ul className="space-y-3 mb-7 flex-grow">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2.5">
                            <CheckCircleSolidIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-600">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <button
                        onClick={() => handlePurchaseMembership(plan)}
                        className={`w-full py-3.5 rounded-xl font-semibold transition-all ${
                          plan.popular
                            ? 'bg-green-600 text-white hover:bg-green-700 shadow-sm hover:shadow-md'
                            : 'bg-gray-900 text-white hover:bg-gray-800'
                        }`}
                      >
                        Request Membership
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits Grid */}
            <div className="max-w-4xl mx-auto animate-smooth-rise animate-delay-500">
              <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">Why Get a Membership?</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => {
                  const Icon = benefit.icon;
                  return (
                    <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex items-start gap-4 hover:shadow-md transition-shadow">
                      <div className="bg-green-50 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="h-5 w-5 text-green-700" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-0.5">{benefit.title}</h3>
                        <p className="text-sm text-gray-500">{benefit.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </main>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthSuccess={(user) => {
          setUser(user);
          fetchUserProfile(user.email);
        }}
      />

      <Footer />
      <WhatsAppButton />
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}

export default MembershipPage;
